import { useState, useEffect } from 'preact/hooks';
import { api, type CheckIn } from '../services/api';

interface LiveCheckInsProps {
  date: string;
  serviceTime?: string;
}

export function LiveCheckIns({ date, serviceTime }: LiveCheckInsProps) {
  const [checkIns, setCheckIns] = useState<CheckIn[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'checked_in' | 'checked_out'>('checked_in');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const loadCheckIns = async () => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await api.getCheckins({
        date,
        serviceTime,
        status: statusFilter,
        search: search || undefined,
        limit: 100,
      });

      setCheckIns(response.checkins);
      setSelectedIds(new Set());
    } catch (err: any) {
      setError(err.message || 'Failed to load check-ins');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadCheckIns();

    const interval = setInterval(loadCheckIns, 15000);
    return () => clearInterval(interval);
  }, [date, serviceTime, statusFilter, search]);

  const handleBatchCheckOut = async () => {
    if (selectedIds.size === 0) return;
    if (!confirm(`Check out ${selectedIds.size} selected child${selectedIds.size !== 1 ? 'ren' : ''}?`)) return;
    try {
      await api.batchCheckoutChildren([...selectedIds]);
      setSelectedIds(new Set());
      loadCheckIns();
    } catch (err: any) {
      alert(err.message || 'Failed to batch check out');
    }
  };

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  if (isLoading && checkIns.length === 0) {
    return (
      <div
        style={{
          background: 'var(--paper)',
          border: '1px solid rgba(25, 38, 49, 0.08)',
          borderRadius: 20,
          padding: 32,
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              style={{
                height: 72,
                borderRadius: 14,
                background: 'rgba(25, 38, 49, 0.03)',
                animation: 'pulse 1.5s cubic-bezier(0.4, 0, 0.6, 1) infinite',
              }}
            />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{
          background: 'var(--coral-soft)',
          border: '1px solid rgba(207, 105, 78, 0.3)',
          borderRadius: 16,
          padding: '20px 24px',
          color: 'var(--coral)',
          fontSize: 14,
        }}
      >
        {error}
      </div>
    );
  }

  return (
    <div
      style={{
        background: 'var(--paper)',
        border: '1px solid rgba(25, 38, 49, 0.08)',
        borderRadius: 20,
        boxShadow: '0 2px 8px rgba(25, 38, 49, 0.02)',
        overflow: 'hidden',
      }}
    >
      {/* ── Header & Search Toolbar ── */}
      <div
        style={{
          padding: '22px 28px',
          borderBottom: '1px solid rgba(25, 38, 49, 0.08)',
          display: 'flex',
          flexDirection: 'column',
          gap: 16,
        }}
      >
        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 14 }}>
          <div>
            <p className="mini-label" style={{ margin: 0 }}>Active Roster</p>
            <h2
              style={{
                fontFamily: 'var(--font-display)',
                fontSize: 20,
                fontWeight: 600,
                letterSpacing: '-0.03em',
                color: 'var(--ink)',
                margin: '2px 0 0',
              }}
            >
              Live Check-Ins ({checkIns.length})
            </h2>
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10 }}>
            {/* Search input */}
            <input
              type="text"
              placeholder="Search by name or code..."
              value={search}
              onChange={(e) => setSearch((e.target as HTMLInputElement).value)}
              style={{
                background: 'rgba(25, 38, 49, 0.03)',
                border: '1px solid rgba(25, 38, 49, 0.1)',
                borderRadius: 12,
                padding: '7px 14px',
                fontSize: 13,
                color: 'var(--ink)',
                outline: 'none',
                width: 200,
              }}
            />

            {/* Status Filter */}
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter((e.target as HTMLSelectElement).value as any)}
              style={{
                background: 'rgba(25, 38, 49, 0.03)',
                border: '1px solid rgba(25, 38, 49, 0.1)',
                borderRadius: 12,
                padding: '7px 14px',
                fontSize: 13,
                color: 'var(--ink)',
                outline: 'none',
              }}
            >
              <option value="checked_in">Currently Checked In</option>
              <option value="checked_out">Checked Out</option>
              <option value="all">All Records</option>
            </select>

            {statusFilter === 'checked_in' && checkIns.length > 0 && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--ink-soft)', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={
                      selectedIds.size === checkIns.filter((c) => c.status === 'checked_in').length &&
                      checkIns.some((c) => c.status === 'checked_in')
                    }
                    onChange={(e) => {
                      if ((e.target as HTMLInputElement).checked) {
                        setSelectedIds(new Set(checkIns.filter((c) => c.status === 'checked_in').map((c) => c.id)));
                      } else {
                        setSelectedIds(new Set());
                      }
                    }}
                    style={{ accentColor: 'var(--marigold)' }}
                  />
                  Select All
                </label>
                <button
                  onClick={handleBatchCheckOut}
                  disabled={selectedIds.size === 0}
                  className="btn-secondary"
                  style={{
                    padding: '6px 14px',
                    fontSize: 12,
                    opacity: selectedIds.size === 0 ? 0.4 : 1,
                    cursor: selectedIds.size === 0 ? 'not-allowed' : 'pointer',
                  }}
                >
                  Checkout Selected{selectedIds.size > 0 ? ` (${selectedIds.size})` : ''}
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ── Check-Ins List ── */}
      <div style={{ maxHeight: 600, overflowY: 'auto' }}>
        {checkIns.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '48px 24px', color: 'var(--ink-soft)' }}>
            <p style={{ fontSize: 14, margin: 0 }}>No check-in records match the current filters.</p>
          </div>
        ) : (
          checkIns.map((checkIn, i) => (
            <div
              key={checkIn.id}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '16px 28px',
                borderBottom: i < checkIns.length - 1 ? '1px solid rgba(25, 38, 49, 0.05)' : 'none',
                transition: 'background 120ms ease',
                gap: 16,
              }}
              onMouseEnter={(e) => {
                (e.currentTarget as HTMLElement).style.background = 'rgba(25, 38, 49, 0.015)';
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLElement).style.background = '';
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 16, flex: 1, minWidth: 0 }}>
                {/* Security Pickup Code Badge */}
                <div
                  style={{
                    background: checkIn.status === 'checked_in' ? 'var(--marigold-soft)' : 'rgba(25, 38, 49, 0.06)',
                    color: checkIn.status === 'checked_in' ? '#7D5507' : 'var(--ink-faint)',
                    fontFamily: 'monospace',
                    fontWeight: 700,
                    fontSize: 20,
                    letterSpacing: '0.04em',
                    padding: '8px 14px',
                    borderRadius: 12,
                    minWidth: 72,
                    textAlign: 'center',
                  }}
                >
                  {checkIn.code}
                </div>

                {/* Child & Family Details */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    <h3
                      style={{
                        fontFamily: 'var(--font-display)',
                        fontSize: 16,
                        fontWeight: 600,
                        letterSpacing: '-0.02em',
                        color: 'var(--ink)',
                        margin: 0,
                      }}
                    >
                      {checkIn.child.firstName} {checkIn.child.lastName}
                    </h3>
                    <span
                      style={{
                        fontSize: 11,
                        padding: '2px 8px',
                        borderRadius: 999,
                        background: 'rgba(25, 38, 49, 0.05)',
                        color: 'var(--ink-soft)',
                        fontWeight: 500,
                      }}
                    >
                      {checkIn.child.ageGroup}
                    </span>
                    <span
                      style={{
                        fontSize: 11,
                        padding: '2px 8px',
                        borderRadius: 999,
                        background: checkIn.status === 'checked_in' ? 'var(--leaf-soft)' : 'rgba(25, 38, 49, 0.06)',
                        color: checkIn.status === 'checked_in' ? '#2F7A4E' : 'var(--ink-soft)',
                        fontWeight: 600,
                      }}
                    >
                      {checkIn.status === 'checked_in' ? 'Checked In' : 'Checked Out'}
                    </span>
                  </div>

                  <p style={{ fontSize: 13, color: 'var(--ink-soft)', margin: '3px 0 0' }}>
                    {checkIn.family.name}
                    <span style={{ color: 'var(--ink-faint)', margin: '0 6px' }}>•</span>
                    <span style={{ color: 'var(--ink-faint)' }}>
                      In: {new Date(checkIn.checkedInAt).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}
                      {checkIn.checkedOutAt && (
                        <> · Out: {new Date(checkIn.checkedOutAt).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', hour12: true })}</>
                      )}
                    </span>
                  </p>

                  {/* Special Information Tags */}
                  {(checkIn.child.allergies || checkIn.child.medicalNotes || checkIn.child.specialInstructions) && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginTop: 6 }}>
                      {checkIn.child.allergies && (
                        <span
                          style={{
                            fontSize: 11,
                            padding: '2px 8px',
                            borderRadius: 6,
                            background: 'var(--coral-soft)',
                            color: 'var(--coral)',
                            fontWeight: 500,
                          }}
                        >
                          Allergies: {checkIn.child.allergies}
                        </span>
                      )}
                      {checkIn.child.medicalNotes && (
                        <span
                          style={{
                            fontSize: 11,
                            padding: '2px 8px',
                            borderRadius: 6,
                            background: 'var(--sky-soft)',
                            color: '#3B7A92',
                            fontWeight: 500,
                          }}
                        >
                          Medical: {checkIn.child.medicalNotes}
                        </span>
                      )}
                      {checkIn.child.specialInstructions && (
                        <span
                          style={{
                            fontSize: 11,
                            padding: '2px 8px',
                            borderRadius: 6,
                            background: 'var(--card-tint-cream)',
                            color: '#8C6207',
                            fontWeight: 500,
                          }}
                        >
                          Note: {checkIn.child.specialInstructions}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Selection Checkbox */}
              {checkIn.status === 'checked_in' && (
                <div>
                  <input
                    type="checkbox"
                    checked={selectedIds.has(checkIn.id)}
                    onChange={() => toggleSelect(checkIn.id)}
                    style={{ width: 18, height: 18, accentColor: 'var(--marigold)', cursor: 'pointer' }}
                  />
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
}
